import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn.js';

interface AnchoredMenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  width?: number;
  /**
   * Which edge of the trigger the menu lines up with. The default lines up the
   * leading edges, so a menu wider than its trigger grows inwards; 'end' hangs
   * it off the trailing edge instead, for a control sitting at the end of a row.
   */
  align?: 'start' | 'end';
  /**
   * Opt-in: the tallest the menu may be. Given, the menu scrolls inside itself,
   * as tall as the room on whichever side of the trigger it opens allows up to
   * this, and a scroll that reaches its end stops there instead of carrying on
   * into the page behind. Left out, the menu is as tall as what it holds, as
   * every other menu here is.
   */
  maxHeight?: number;
  /** Contextual tools prefer the space above their trigger when it fits. */
  preferAbove?: boolean;
  children: ReactNode;
}

/** The gap between the trigger and the menu, on whichever side it opens. */
const GAP = 6;

/**
 * Where a menu that scrolls inside itself opens, and how tall it may be.
 *
 * Below the trigger while what it holds (up to its cap) fits there, else on the
 * side with more room — on a phone with the keyboard up the room below can be
 * a few rows. Its height is that side's room, never more than the cap, so the
 * list scrolls within the screen rather than running off its end.
 */
export function fitMenu({
  anchorTop,
  anchorBottom,
  roomTop,
  roomBottom,
  natural,
  cap,
  preferAbove = false,
}: {
  anchorTop: number;
  anchorBottom: number;
  /** The usable band of the screen: inside the safe areas and above the keyboard. */
  roomTop: number;
  roomBottom: number;
  /** What the menu would be with nothing cut off. */
  natural: number;
  cap: number;
  preferAbove?: boolean;
}): { below: boolean; maxHeight: number } {
  const spaceBelow = roomBottom - (anchorBottom + GAP);
  const spaceAbove = anchorTop - GAP - roomTop;
  const below = preferAbove && Math.min(natural,cap)<=spaceAbove ? false : Math.min(natural, cap) <= spaceBelow || spaceBelow >= spaceAbove;
  return { below, maxHeight: Math.max(0, Math.min(cap, below ? spaceBelow : spaceAbove)) };
}

/**
 * Every open menu portals to `document.body`, so a menu opened from inside
 * another menu is its DOM *sibling*, not its descendant — and the outer menu's
 * outside-click check would read a click on the inner one as a click somewhere
 * else and close itself, taking the inner menu down before it could act on the
 * click. Containment cannot see the nesting, so the nesting is carried in
 * React, where it is real: an open menu registers its own element with the menu
 * it was rendered inside, and passes the registration further up so depth is
 * not limited to one.
 */
type RegisterNested = (element: HTMLElement) => () => void;
const NestedMenus = createContext<RegisterNested | null>(null);

/** Renders a dropdown in a portal, anchored to a trigger — never clipped by a scroll container. */
export function AnchoredMenu({
  open,
  onClose,
  anchorRef,
  width = 176,
  align = 'start',
  maxHeight: cap,
  preferAbove = false,
  children,
}: AnchoredMenuProps) {
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight?: number }>({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const nested = useRef(new Set<HTMLElement>());
  const registerUp = useContext(NestedMenus);

  const register = useCallback<RegisterNested>(
    (element) => {
      nested.current.add(element);
      const release = registerUp?.(element);
      return () => {
        nested.current.delete(element);
        release?.();
      };
    },
    [registerUp],
  );

  useEffect(() => {
    if (!open || !registerUp) return;
    const element = menuRef.current;
    return element ? registerUp(element) : undefined;
  }, [open, registerUp]);

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Both edges are physical, so which one the menu is pinned to swaps with
      // the writing direction: in Hebrew the leading edge is the right one.
      const rtl = document.documentElement.dir === 'rtl';
      const pinned = (align === 'start') === rtl ? r.right - width : r.left;
      const styles = getComputedStyle(document.documentElement);
      const inset = (prop: string) => Number.parseFloat(styles.getPropertyValue(prop)) || 0;
      const padTop = 8 + inset('--safe-area-top');
      const padRight = 8 + inset('--safe-area-right');
      const padBottom = 8 + inset('--safe-area-bottom');
      const padLeft = 8 + inset('--safe-area-left');
      const left = Math.min(Math.max(pinned, padLeft), window.innerWidth - width - padRight);

      if (cap !== undefined) {
        const menu = menuRef.current;
        // What it would be uncut: its border, and all that it can scroll through.
        const natural = menu ? menu.offsetHeight - menu.clientHeight + menu.scrollHeight : 0;
        // The visual viewport where there is one: on a phone it ends at the
        // keyboard, and rows drawn under the keyboard cannot be reached.
        const viewport = window.visualViewport;
        const roomTop = (viewport?.offsetTop ?? 0) + padTop;
        const roomBottom = (viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight) - padBottom;
        const fit = fitMenu({ anchorTop: r.top, anchorBottom: r.bottom, roomTop, roomBottom, natural, cap, preferAbove });
        const height = Math.min(natural, fit.maxHeight);
        const next = { top: fit.below ? r.bottom + GAP : Math.max(r.top - GAP - height, roomTop), left, maxHeight: fit.maxHeight };
        // Kept when nothing moved: the resize observer below calls this, and a
        // new object every time would draw the menu again for nothing.
        setPos((was) => (was.top === next.top && was.left === next.left && was.maxHeight === next.maxHeight ? was : next));
        return;
      }

      const height = menuRef.current?.offsetHeight ?? 0;
      const below = r.bottom + 6;
      const above = r.top - 6 - height;
      const top = preferAbove && above>=padTop ? above : below + height <= window.innerHeight - padBottom || above < padTop ? below : above;
      setPos({
        top: Math.max(Math.min(top, window.innerHeight - padBottom - height), padTop),
        left,
      });
    };
    update();
    if (cap !== undefined) {
      // The menu scrolls itself, and its own scroll is no reason to move it.
      const onScroll = (event: Event) => {
        if (!menuRef.current?.contains(event.target as Node)) update();
      };
      // Placed above the trigger it hangs from its top edge, so a search that
      // makes it shorter would leave a gap under it; it is placed again instead.
      const resized = new ResizeObserver(update);
      if (menuRef.current) resized.observe(menuRef.current);
      window.addEventListener('resize', update);
      window.addEventListener('scroll', onScroll, true);
      window.visualViewport?.addEventListener('resize', update);
      return () => {
        resized.disconnect();
        window.removeEventListener('resize', update);
        window.removeEventListener('scroll', onScroll, true);
        window.visualViewport?.removeEventListener('resize', update);
      };
    }
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef, width, align, cap, preferAbove]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      for (const element of nested.current) if (element.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={cn(
        'fixed z-[9999] bg-card border border-border rounded-xl shadow-2xl animate-fade-up',
        cap !== undefined ? 'overflow-y-auto overscroll-contain' : 'overflow-hidden',
      )}
      style={{ top: pos.top, left: pos.left, width, maxHeight: pos.maxHeight }}
    >
      <NestedMenus.Provider value={register}>{children}</NestedMenus.Provider>
    </div>,
    document.body,
  );
}
